<template>
  <v-card
    v-if="Object.keys(product).length > 0"
    class="add-to-trade-ticket"
  >
    <v-toolbar dark color="primary">
      <v-toolbar-title
        class="headline font-weight-regular font-weight-bold"
      >
        {{ product.name }}
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon dark @click="close">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>
    <v-card-text>
      <v-container>
        <v-form ref="form">
          <v-simple-table
            class="elevation-0"
          >
            <thead>
              <tr>
                <th 
                  v-for="header in tableHeaders"
                  :key="header.value" 
                  :class="['text-' + (header.align || 'left'), header.class, header.value === 'quantity' ? 'qty-header-cell' : '']"
                  :style="header.width ? { width: header.width, 'min-width': header.width } : {}"
                >
                  {{ header.text }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="tableItems.length > 0">
                <!-- Quantity Cell -->
                <td class="text-center">
                  <number-field
                    ref="quantityInput"
                    :options="
                        product.allowFractionalQty
                          ? { type: `fractionalQty` }
                          : { type: `qty` }
                      "
                    :min="getMin(product, buySell)"
                    :max="getMax(product, buySell)"
                    :rules="getRulesForItem(product, buySell)"
                    v-model="updateQty"
                    class="number-field-qty"
                    v-precision.group="getMaxAllowedQtyDecimals(product)"
                  />
                </td>
                <!-- Premium Cell (conditionally rendered) -->
                <td class="text-center" v-if="getSelectedTradingRole !== TradingRoles.SALES_REP">
                  {{ tableItems[0].premium }}
                </td>
                <!-- Unit Price Cell -->
                <td class="text-center">
                  <span style="padding-left: 12px;">{{ tableItems[0].unitPrice | toCurrency }}</span>
                </td>
                <!-- Total Cell -->
                <td class="text-center">
                  <span class="font-weight-bold">{{ tableItems[0].total | toCurrency }}</span>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
          <p
            v-if="product.description && buySell === 'buy'"
            class="pt-1 pb-2 text-center"
          >
            {{ product.description }}
          </p>
          <p
            v-if="product.saleNote && buySell === 'sell'"
            class="pt-1 pb-2 text-center"
          >
            {{ product.saleNote }}
          </p>
          <v-divider class="mt-5 mb-5"></v-divider>
          <v-row class="mt-4" dense justify="center">
            <template v-if="showLimitOrder()">
              <v-col cols="12" md="auto" class="text-center">
                <v-btn color="primary" @click="addThen(true)">
                  Add &amp; View Trade Ticket
                </v-btn>
              </v-col>
              <v-col cols="12" md="auto" class="text-center">
                <v-btn color="secondary" @click="addThen(false)">
                  Add &amp; Continue Trading
                </v-btn>
              </v-col>
              <v-col cols="12" md="auto" class="text-center">
                <buy-sell-button
                  :item="product"
                  :buy-sell="buySell"
                  :quantity="updateQty"
                  :show-formulas="false"
                  height="36px"
                  limitOrder="true"
                  v-on:added="$emit('close')"
                ></buy-sell-button>
              </v-col>
            </template>
            <template v-else>
              <v-col cols="12" md="auto" class="text-center">
                <v-btn color="primary" @click="addThen(true)">
                  Add &amp; View Trade Ticket
                </v-btn>
              </v-col>
              <v-col cols="12" md="auto" class="text-center">
                <v-btn color="secondary" @click="addThen(false)">
                  Add &amp; Continue Trading
                </v-btn>
              </v-col>
            </template>
          </v-row>
          <volume-discount-table
            :item="product"
            :buy-sell="buySell"
          ></volume-discount-table>
        </v-form>
      </v-container>
    </v-card-text>
  </v-card>
</template>
<script>
import productMixin from '@/mixins/product'
import guardsMixin from '@/mixins/guards'
import {mapGetters, mapState} from 'vuex'
import VolumeDiscountTable from '@/components/catalog/VolumeDiscountTable'
import {TradingRoles} from '@/util/constants'
import NumberField from '@/components/NumberField'
import BuySellButton from '@/components/cart/BuySellButton'

export default {
  mixins: [productMixin, guardsMixin],
  components: {
    NumberField,
    VolumeDiscountTable,
    BuySellButton,
  },
  watch: {
    shown: {
      immediate: true,
      handler(val) {
        if (val) this.resetState()
      },
    },
    updateQty: {
      async handler(quantity) {
        this.product = await this.$store.dispatch('fetchProductWithQuantity', {
          id: this.product.id,
          quantity,
        })
        this.$nextTick(() => { // Ensure DOM is updated before resizing
          this.resizeInput();
        });
      },
    },
  },
  data() {
    return {
      updateQty: null,
      product: null,
      TradingRoles,
      textMeasurer: null, // For measuring text width
    }
  },
  computed: {
    ...mapState({
      spotPrices: (state) => state.user.spotPrices,
    }),
    ...mapGetters(['getSelectedCompany', 'getSelectedTradingRole']),
    tableHeaders() {
      const headers = [
        { text: 'Qty', value: 'quantity', sortable: false, align: 'center', width: 'auto' /* Adjusted for dynamic input */ },
        { text: 'Unit Price', value: 'unitPrice', sortable: false, align: 'center' },
        { text: 'Total', value: 'total', sortable: false, align: 'center', class: 'font-weight-bold' }
      ];
      if (this.getSelectedTradingRole !== this.TradingRoles.SALES_REP) {
        headers.splice(1, 0, { text: 'Premium', value: 'premium', sortable: false, align: 'center' });
      }
      return headers;
    },
    tableItems() {
      const item = {
        unitPrice: this.computeUnitPrice(this.buySell),
        total: this.updateQty * this.computeUnitPrice(this.buySell)
      };
      if (this.getSelectedTradingRole !== this.TradingRoles.SALES_REP) {
        item.premium = this.getPremiumText(this.product, this.buySell);
      }
      item.quantity = this.updateQty; 
      return [item];
    }
  },
  props: ['shown', 'selectedProduct', 'buySell', 'quantity'],
  methods: {
    resizeInput() {
      if (!this.textMeasurer || !this.$refs.quantityInput || !this.$refs.quantityInput.$el) {
        return;
      }
      const inputEl = this.$refs.quantityInput.$el.querySelector('input');
      if (!inputEl) return;

      // Use the textMeasurer span to get the width of the text
      this.textMeasurer.textContent = inputEl.value || inputEl.placeholder || '0'; // Use placeholder or '0' if empty
      const textWidth = this.textMeasurer.offsetWidth;

      // Set a minimum width (e.g., for 2-3 characters) and allow expansion
      const minWidth = 35; // Adjust as needed
      const newWidth = Math.max(minWidth, textWidth + 10); // Add some padding

      inputEl.style.width = `${newWidth}px`;
      
      // Adjust the number-field component's wrapper if necessary
      // This depends on how NumberField is structured internally.
      // If NumberField's root element isn't already shrink-wrapping, this might be needed.
      // this.$refs.quantityInput.$el.style.width = 'auto'; 
      // this.$refs.quantityInput.$el.style.display = 'inline-block';

    },
    showLimitOrder() {
      return this.buySell === `buy` && this.limitOrdersEnabled()
    },
    resetState() {
      const initialQty = this.quantity || this.getDefaultQty(this.selectedProduct, this.buySell)
      const numericQty = Number(initialQty)
      this.updateQty = Number.isInteger(numericQty) ? parseInt(initialQty, 10) : numericQty
      this.product = this.selectedProduct
      this.$nextTick(() => { // Ensure DOM is updated before resizing
        this.resizeInput();
      });
    },
    async addThen(showTradeTicket) {
      if (this.$refs.form.validate()) {
        try {
          const payload = {
            item: this.product,
            quantity: this.updateQty,
            buySell: this.buySell,
          }
          await this.$store.dispatch('updateItemQuantity', payload)
          await this.$store.dispatch(
            'updateItemRetailData',
            Object.assign(
              {},
              payload,
              this.getRetailMarkups(
                this.product,
                this.getSelectedCompany,
                this.buySell
              )
            )
          )
          if (showTradeTicket)
            window._VMA.$emit('SHOW_TRADE_TICKET', {
              defaultBuySell: this.buySell,
            })
          this.$emit('close') 
        } catch (error) {
          window._VMA.$emit('SHOW_SNACKBAR', {
            text: error.message,
            color: 'error',
          })
        }
      }
    },
    removeItemFromCart() {
      const payload = {
        item: {item: this.product},
        buySell: this.buySell,
      }
      this.$store.dispatch('removeItemFromCart', payload)
      window._VMA.$emit('SHOW_SNACKBAR', {
        text: `Product has been removed from your trade ticket.`,
        color: 'info',
        linkToTradeTicket: true,
      })
      this.close()
    },
    close() {
      this.$emit('close')
    },
    computeUnitPrice: function (buySell) {
      return buySell === 'buy'
        ? this.computePrices(
          this.product,
          this.spotPrices,
          this.getSelectedCompany,
          this.getSelectedTradingRole
        ).ask
        : this.computePrices(
          this.product,
          this.spotPrices,
          this.getSelectedCompany,
          this.getSelectedTradingRole
        ).bid
    },
  },
  mounted() {
    // Create a hidden span to measure text width
    this.textMeasurer = document.createElement('span');
    this.textMeasurer.style.visibility = 'hidden';
    this.textMeasurer.style.position = 'absolute';
    this.textMeasurer.style.whiteSpace = 'nowrap';
    // Attempt to match input's font style for accurate measurement
    if (this.$refs.quantityInput && this.$refs.quantityInput.$el) {
        const inputEl = this.$refs.quantityInput.$el.querySelector('input');
        if (inputEl) {
            const computedStyle = window.getComputedStyle(inputEl);
            this.textMeasurer.style.fontFamily = computedStyle.fontFamily;
            this.textMeasurer.style.fontSize = computedStyle.fontSize;
            this.textMeasurer.style.fontWeight = computedStyle.fontWeight;
            this.textMeasurer.style.letterSpacing = computedStyle.letterSpacing;
            this.textMeasurer.style.padding = computedStyle.padding;

        }
    }
    document.body.appendChild(this.textMeasurer);
    this.$nextTick(() => {
        this.resizeInput();
    });
  },
  beforeDestroy() {
    // Clean up the textMeasurer element
    if (this.textMeasurer && this.textMeasurer.parentNode) {
      this.textMeasurer.parentNode.removeChild(this.textMeasurer);
      this.textMeasurer = null;
    }
  },
}
</script>

<style>
.add-to-trade-ticket .v-messages__message {
  word-break: keep-all;
}
.add-to-trade-ticket .v-toolbar__title {
  font-size: 1rem !important; /* Match body font size */
}
/* Remove white background from number input */
.add-to-trade-ticket .v-input__slot {
  background-color: transparent !important;
}
/* Align table cell contents vertically */
.add-to-trade-ticket tbody td {
  vertical-align: middle !important;
  height: 48px !important;
}

/* Target the number field specifically within the trade ticket table */
.add-to-trade-ticket tbody td .number-field-qty {
  margin: 0 !important;
  padding: 0 !important;
  /* Allow the component to size itself based on its content */
  display: inline-block !important; 
  width: auto !important; 
  vertical-align: middle; /* Better alignment with other cell content */
}

.add-to-trade-ticket tbody td .number-field-qty input {
  text-align: center !important;
  padding: 0 4px !important; 
  height: 36px !important;
  line-height: 36px !important;
  /* width will be set dynamically by JS */
  min-width: 35px; /* Minimum width for the input */
  box-sizing: border-box; /* Ensure padding doesn't add to width */
}

/* Adjust the wrapper if needed to prevent extra space */
.add-to-trade-ticket tbody td .v-input__slot {
  border: none !important;
  padding: 0 !important;
  min-height: auto !important;
  margin-bottom: 0 !important;
}

.add-to-trade-ticket tbody td .v-input {
  padding-top: 0 !important;
  margin-top: 0 !important;
}

/* Allow validation messages to show for v-text-field__details */

.add-to-trade-ticket .qty-header-cell {
  /* This will allow the header to shrink/grow with the input field if table-layout:auto is used or implied */
  /* For fixed layouts, this might not have a huge impact without other adjustments */
  /* but helps keep the Qty column as small as the content allows */
  width: 1%; /* Special value for table layout algorithms, tries to make it as small as possible */
  white-space: nowrap; /* Prevents header text from wrapping if it becomes too small */
}
</style>
